import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { DEFAULT_TOOL_FOUP_CAPACITY } from '../../features/equipment/equipmentConstants';
import {
  AMHS_STREAM_DISPATCH_DESCRIPTIONS,
  AMHS_STREAM_DISPATCH_LABELS,
  AMHS_STREAM_DISPATCH_POLICIES,
  applyStreamDispatchPolicy,
  dispatchPolicyFromRequest,
} from '../../features/simulation/amhsStreamDispatch';
import {
  fromAmhsStreamWireRequest,
  toAmhsStreamWireRequest,
} from '../../features/simulation/amhsStreamWaitTimes';
import { parseAmhsStreamRequestJson } from '../../features/simulation/validateAmhsStreamRequest';
import type { AmhsStreamOhtAssignment, AmhsStreamRunRequest } from '../../types/amhsStream';
import { useProjectProcessTypes } from '../../hooks/useProjectProcessTypes';

type ReviewSimulationConfigModalProps = {
  request: AmhsStreamRunRequest;
  generatedRequest: AmhsStreamRunRequest;
  foupCount: number;
  hasOverride: boolean;
  onPersist: (request: AmhsStreamRunRequest, foupCount: number) => void;
  onApply: (request: AmhsStreamRunRequest, isTestingOverride: boolean) => void;
  onClearOverride: () => void;
  onClose: () => void;
};

const STEPS = ['FOUPs and OHTs', 'Tools', 'Stockers', 'Dedicated OHTs'] as const;

/** Temporarily hidden: the dispatch policy picker and its OR-Tools options in step 1. */
const SHOW_DISPATCH_POLICY_SECTION = false;

/** Temporarily hidden: Headway, Continuous and Horizon in step 1. */
const SHOW_ADVANCED_LIVE_FIELDS = false;

/** Temporarily hidden: the "Enforce node capacity" checkbox in step 1 (the request default still applies). */
const SHOW_ENFORCE_NODE_CAPACITY = false;

/** Temporarily hidden: the "Reset to generated config" footer button. */
const SHOW_RESET_TO_GENERATED = false;

function formatJson(request: AmhsStreamRunRequest): string {
  return JSON.stringify(toAmhsStreamWireRequest(request), null, 2);
}

function junctionWaitSec(request: AmhsStreamRunRequest): number {
  const first = request.trackNetwork.trackNetwork.junctions[0];
  return typeof first?.waitSec === 'number' && Number.isFinite(first.waitSec) ? first.waitSec : 0;
}

const ReviewSimulationConfigModal: React.FC<ReviewSimulationConfigModalProps> = ({
  request,
  generatedRequest,
  foupCount,
  hasOverride,
  onPersist,
  onApply,
  onClearOverride,
  onClose,
}) => {
  const [step, setStep] = useState(0);
  const [showJson, setShowJson] = useState(false);
  const [draftRequest, setDraftRequest] = useState(generatedRequest);
  const [draftFoupCount, setDraftFoupCount] = useState(foupCount);
  const [json, setJson] = useState(() => formatJson(request));
  const [jsonEdited, setJsonEdited] = useState(false);
  const parsed = useMemo(() => parseAmhsStreamRequestJson(json), [json]);
  const tools = draftRequest.tools.tools;
  const stockers = draftRequest.stockers.stockers;
  const ohts = draftRequest.ohts.ohts;
  const dedicatedOhts = ohts.filter((oht) => oht.ohtMode === 'dedicated');
  const dispatchPolicy = dispatchPolicyFromRequest(draftRequest);
  const { processTypes } = useProjectProcessTypes([
    ...tools.map((tool) => tool.toolType),
    ...stockers.map((stocker) => stocker.processType),
    ...dedicatedOhts.flatMap((oht) => [oht.toolType]),
  ]);

  const goToStep = (nextStep: number) => {
    setStep(nextStep);
  };

  const updateTool = (
    toolId: string,
    changes: Partial<AmhsStreamRunRequest['tools']['tools'][number]>
  ) => {
    setDraftRequest((current) => ({
      ...current,
      tools: {
        tools: current.tools.tools.map((tool) =>
          tool.toolId === toolId ? { ...tool, ...changes } : tool
        ),
      },
    }));
  };

  const updateStocker = (
    stockerId: string,
    changes: Partial<AmhsStreamRunRequest['stockers']['stockers'][number]>
  ) => {
    setDraftRequest((current) => ({
      ...current,
      stockers: {
        stockers: current.stockers.stockers.map((stocker) => {
          if (stocker.stockerId !== stockerId) return stocker;
          const next = { ...stocker, ...changes };
          if (changes.foupCapacity != null) {
            const rawFoupCapacity = Math.ceil(changes.foupCapacity / 2);
            next.rawFoupCapacity = rawFoupCapacity;
            next.processedFoupCapacity = changes.foupCapacity - rawFoupCapacity;
          }
          next.materialFlow = next.outsideStocker || !next.processType ? 'passThrough' : 'process';
          return next;
        }),
      },
    }));
  };

  const updateOht = (
    ohtId: string,
    changes: Partial<AmhsStreamRunRequest['ohts']['ohts'][number]>
  ) => {
    setDraftRequest((current) => ({
      ...current,
      ohts: {
        ...current.ohts,
        ohts: current.ohts.ohts.map((oht) => (oht.ohtId === ohtId ? { ...oht, ...changes } : oht)),
      },
    }));
  };

  const updateOhtAssignment = (changes: Partial<AmhsStreamOhtAssignment>) => {
    setDraftRequest((current) => ({
      ...current,
      ohtAssignment: { ...current.ohtAssignment, ...changes },
    }));
  };

  const applyJsonOverride = useCallback(() => {
    if (!parsed.request || !jsonEdited) return;
    onApply(fromAmhsStreamWireRequest(parsed.request), true);
  }, [jsonEdited, onApply, parsed.request]);

  const closeModal = useCallback(() => {
    applyJsonOverride();
    onClose();
  }, [applyJsonOverride, onClose]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeModal]);

  const resetToGenerated = () => {
    setDraftRequest(generatedRequest);
    setDraftFoupCount(foupCount);
    setJson(formatJson(generatedRequest));
    setJsonEdited(false);
    onClearOverride();
  };

  const saveRequest = () => {
    onPersist(draftRequest, draftFoupCount);
    onClose();
  };

  const openJsonView = () => {
    if (!jsonEdited) {
      setJson(formatJson(hasOverride ? request : draftRequest));
    }
    setShowJson(true);
  };

  const hideJsonView = () => {
    applyJsonOverride();
    setShowJson(false);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-90 flex items-center justify-center bg-gray-950/50 px-4 py-5 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="review-simulation-config-title"
      onClick={closeModal}
    >
      <div
        className="flex max-h-full w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-4 py-3">
          <div>
            <div className="flex items-center gap-2">
              <h2
                id="review-simulation-config-title"
                className="text-sm font-medium text-slate-900"
              >
                Review configuration
              </h2>
              {hasOverride && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                  Testing override active
                </span>
              )}
            </div>
            <p className="mt-0.5 text-[10px] text-slate-500">
              Changes in steps 1–4 save back to the matching project, tool, stocker, and OHT
              properties.
            </p>
          </div>
          <button
            type="button"
            onClick={closeModal}
            aria-label="Close configuration review"
            className="rounded px-2 py-1 text-xs font-normal text-slate-500 hover:bg-slate-100 hover:text-slate-800"
          >
            Close
          </button>
        </header>

        <nav className="border-b border-slate-200 bg-slate-50 px-4 py-2" aria-label="Review steps">
          <ol className="grid grid-cols-4 gap-1.5">
            {STEPS.map((label, index) => (
              <li key={label}>
                <button
                  type="button"
                  onClick={() => {
                    if (showJson) hideJsonView();
                    goToStep(index);
                  }}
                  className={`flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left text-[10px] font-medium transition-colors ${
                    step === index
                      ? 'bg-sky-600 text-white'
                      : index < step
                        ? 'bg-sky-100 text-sky-800 hover:bg-sky-200'
                        : 'bg-white text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] ${
                      step === index ? 'bg-white/20' : 'bg-slate-100'
                    }`}
                  >
                    {index + 1}
                  </span>
                  <span className="truncate">{label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>

        <main className="min-h-0 flex-1 overflow-y-auto px-4 py-3 scrollbar-thin">
          {showJson && (
            <div className="space-y-2.5">
              <ReviewHeading
                title="WebSocket run request JSON"
                description="This is the complete runRequest sent when a new live stream starts. Drop-to-pick, junction, tool, and stocker waits are shown as sim seconds. Edits are a temporary testing override and are not saved to project, tool, stocker, or OHT properties."
              />
              <div className="rounded border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[10px] text-amber-900">
                Applying this JSON sends it on the next live start only. Source-backed fields in
                steps 1–4 are left unchanged.
              </div>
              <textarea
                value={json}
                onChange={(event) => {
                  const next = event.target.value;
                  setJson(next);
                  setJsonEdited(true);
                  const nextParsed = parseAmhsStreamRequestJson(next);
                  if (nextParsed.request) {
                    onApply(fromAmhsStreamWireRequest(nextParsed.request), true);
                  }
                }}
                spellCheck={false}
                aria-label="Live simulation request JSON"
                className={`h-96 w-full resize-y rounded border bg-white p-2.5 font-mono text-[10px] font-normal leading-relaxed text-slate-700 outline-none ${
                  parsed.errors.length > 0
                    ? 'border-red-400 ring-2 ring-red-100'
                    : 'border-slate-300 focus:border-sky-500 focus:ring-1 focus:ring-sky-100'
                }`}
              />
              {parsed.errors.length > 0 ? (
                <div className="rounded border border-red-200 bg-red-50 px-2.5 py-1.5 text-[10px] text-red-900">
                  <p className="font-semibold">
                    Fix {parsed.errors.length} configuration{' '}
                    {parsed.errors.length === 1 ? 'error' : 'errors'} before applying:
                  </p>
                  <ul className="mt-1 max-h-28 list-disc space-y-0.5 overflow-y-auto pl-4 scrollbar-thin">
                    {parsed.errors.map((error, index) => (
                      <li key={`${error}-${index}`}>{error}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-[10px] font-normal text-emerald-700">
                  JSON is valid and ready to use for the next live start.
                </p>
              )}
            </div>
          )}

          {!showJson && step === 0 && (
            <div className="space-y-3">
              {SHOW_DISPATCH_POLICY_SECTION && (
                <>
                  <ReviewHeading
                    title="OHT dispatch policy"
                    description="Choose how ready hops are activated and how free vehicles are assigned on the live stream."
                  />
                  <fieldset className="grid gap-2 sm:grid-cols-3">
                    <legend className="sr-only">OHT dispatch policy</legend>
                    {AMHS_STREAM_DISPATCH_POLICIES.map((policy) => (
                      <label
                        key={policy}
                        className={`flex cursor-pointer flex-col rounded border p-2.5 transition-colors ${
                          dispatchPolicy === policy
                            ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="dispatchPolicy"
                            checked={dispatchPolicy === policy}
                            onChange={() =>
                              setDraftRequest((current) =>
                                applyStreamDispatchPolicy(current, policy)
                              )
                            }
                            className="h-3.5 w-3.5 accent-sky-600"
                          />
                          <span className="text-[11px] font-medium text-slate-900">
                            {AMHS_STREAM_DISPATCH_LABELS[policy]}
                          </span>
                        </span>
                        <span className="mt-1 pl-5 text-[9px] leading-snug text-slate-500">
                          {AMHS_STREAM_DISPATCH_DESCRIPTIONS[policy]}
                        </span>
                      </label>
                    ))}
                  </fieldset>
                  {dispatchPolicy === 'ortools' && (
                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      <NumberEditorCard
                        label="OR-Tools time limit"
                        value={draftRequest.ohtAssignment.ortoolsTimeLimitS ?? 0.05}
                        min={0.01}
                        step={0.01}
                        suffix="s"
                        detail="Solver budget per live dispatch tick."
                        onChange={(ortoolsTimeLimitS) => updateOhtAssignment({ ortoolsTimeLimitS })}
                      />
                      <NumberEditorCard
                        label="Shared OHT min gain"
                        value={draftRequest.ohtAssignment.sharedOhtMinGainSec ?? 30}
                        min={0}
                        step={1}
                        suffix="s sim"
                        detail="Minimum time saved before a shared OHT assist is considered."
                        onChange={(sharedOhtMinGainSec) =>
                          updateOhtAssignment({ sharedOhtMinGainSec })
                        }
                      />
                      <BooleanEditorCard
                        label="Intelligent shared OHT"
                        value={draftRequest.ohtAssignment.intelligentSharedOht ?? false}
                        onChange={(intelligentSharedOht) =>
                          updateOhtAssignment({ intelligentSharedOht })
                        }
                      />
                      <BooleanEditorCard
                        label="Intelligent dedicated assist"
                        value={draftRequest.ohtAssignment.intelligentDedicatedAssist ?? false}
                        onChange={(intelligentDedicatedAssist) =>
                          updateOhtAssignment({ intelligentDedicatedAssist })
                        }
                      />
                      <BooleanEditorCard
                        label="Dedicated jobs first"
                        value={draftRequest.ohtAssignment.dedicatedJobsFirst ?? true}
                        onChange={(dedicatedJobsFirst) =>
                          updateOhtAssignment({ dedicatedJobsFirst })
                        }
                      />
                    </div>
                  )}
                </>
              )}
              <ReviewHeading
                title="Live speed and FOUP settings"
                description="Values generated from the current Simulation panel and project configuration."
              />
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                <NumberEditorCard
                  label="Live simulation speed"
                  value={draftRequest.liveStream.simSpeed}
                  min={0.1}
                  step={0.5}
                  suffix="×"
                  detail="Simulation seconds per real second"
                  onChange={(simSpeed) =>
                    setDraftRequest((current) => ({
                      ...current,
                      liveStream: { ...current.liveStream, simSpeed },
                    }))
                  }
                />
                <NumberEditorCard
                  label="OHT Pickup time"
                  value={draftRequest.liveStream.pickupHoldWallSec}
                  min={0}
                  step={0.1}
                  suffix="s"
                  detail="Time taken to pick up a FOUP from a port."
                  onChange={(pickupHoldWallSec) =>
                    setDraftRequest((current) => ({
                      ...current,
                      liveStream: { ...current.liveStream, pickupHoldWallSec },
                    }))
                  }
                />
                <NumberEditorCard
                  label="OHT Drop time"
                  value={draftRequest.liveStream.dropHoldWallSec}
                  min={0}
                  step={0.1}
                  suffix="s"
                  detail="Time taken to drop a FOUP on a port"
                  onChange={(dropHoldWallSec) =>
                    setDraftRequest((current) => ({
                      ...current,
                      liveStream: { ...current.liveStream, dropHoldWallSec },
                    }))
                  }
                />
                <NumberEditorCard
                  label="OHT drop to pickup delay"
                  value={draftRequest.handling.dropToPickSec}
                  min={0.1}
                  step={0.1}
                  suffix="s"
                  detail="Delay between an OHT drop and the next pickup"
                  onChange={(dropToPickSec) =>
                    setDraftRequest((current) => ({
                      ...current,
                      handling: { ...current.handling, dropToPickSec },
                    }))
                  }
                />
                <NumberEditorCard
                  label="Junction wait"
                  value={junctionWaitSec(draftRequest)}
                  min={0}
                  step={0.1}
                  suffix="s"
                  detail="Time taken by OHT to switch tracks using a circle junction"
                  onChange={(waitSec) =>
                    setDraftRequest((current) => ({
                      ...current,
                      trackNetwork: {
                        ...current.trackNetwork,
                        trackNetwork: {
                          ...current.trackNetwork.trackNetwork,
                          junctions: current.trackNetwork.trackNetwork.junctions.map(
                            (junction) => ({ ...junction, waitSec })
                          ),
                        },
                      },
                    }))
                  }
                />
                {SHOW_ENFORCE_NODE_CAPACITY && (
                  <BooleanEditorCard
                    label="Enforce node capacity"
                    detail="One OHT per stocker/tool dock at a time. Junctions are always protected. Off: docks are shared and the whole fleet stays busy; on: no two OHTs on a dock, but the OHT resting on a dock takes its next job."
                    value={draftRequest.collisionAvoidance.enforceNodeCapacity}
                    onChange={(enforceNodeCapacity) =>
                      setDraftRequest((current) => ({
                        ...current,
                        collisionAvoidance: {
                          ...current.collisionAvoidance,
                          enforceNodeCapacity,
                        },
                      }))
                    }
                  />
                )}
              </div>
              {SHOW_ADVANCED_LIVE_FIELDS && (
                <>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <NumberEditorCard
                      label="Headway"
                      value={draftRequest.collisionAvoidance.headwaySec}
                      min={0}
                      step={1}
                      suffix="s sim"
                      detail="Minimum time gap between OHTs on the same track segment."
                      onChange={(headwaySec) =>
                        setDraftRequest((current) => ({
                          ...current,
                          collisionAvoidance: { ...current.collisionAvoidance, headwaySec },
                        }))
                      }
                    />
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <BooleanEditorCard
                      label="Continuous"
                      value={draftRequest.liveStream.continuous}
                      onChange={(continuous) =>
                        setDraftRequest((current) => ({
                          ...current,
                          liveStream: { ...current.liveStream, continuous },
                        }))
                      }
                    />
                    <NumberEditorCard
                      label="Horizon"
                      value={draftRequest.liveStream.horizonSec}
                      min={0.1}
                      step={1}
                      suffix="s"
                      onChange={(value) =>
                        setDraftRequest((current) => ({
                          ...current,
                          liveStream: { ...current.liveStream, horizonSec: value },
                        }))
                      }
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {!showJson && step === 1 && (
            <div className="space-y-3">
              <ReviewHeading
                title={`Tools (${tools.length})`}
                description="Process times (wall / real seconds) and tool properties. Sent as value × simSpeed."
              />
              <div className="overflow-hidden rounded border border-slate-200">
                <table className="w-full table-fixed border-collapse text-[11px]">
                  <thead className="bg-slate-50 text-[9px] uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium">Tool</th>
                      <th className="w-[26%] px-2 py-1.5 text-left font-medium">Type</th>
                      <th className="w-32 px-2 py-1.5 text-left font-medium">Process time (s)</th>
                      <th className="w-32 px-2 py-1.5 text-left font-medium">FOUP capacity</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {tools.map((tool) => (
                      <tr key={tool.toolId} className="text-slate-700">
                        <td className="px-2 py-1.5 align-top">
                          <input
                            value={tool.name}
                            onChange={(event) =>
                              updateTool(tool.toolId, { name: event.target.value })
                            }
                            aria-label={`Name for ${tool.toolId}`}
                            className="h-7 w-full rounded border border-slate-300 px-2 text-[11px] font-normal text-slate-900"
                          />
                          <div className="font-mono text-[9px] text-slate-400">
                            ID: {tool.toolId}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          <select
                            value={tool.toolType}
                            onChange={(event) =>
                              updateTool(tool.toolId, {
                                toolType: event.target.value,
                                processFamily: event.target.value,
                              })
                            }
                            aria-label={`Type for ${tool.toolId}`}
                            className="h-7 w-full rounded border border-slate-300 px-2 text-[11px] font-normal uppercase"
                          >
                            {processTypes.map((processType) => (
                              <option key={processType} value={processType}>
                                {processType}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          <div className="flex h-7 items-center gap-1">
                            <input
                              type="number"
                              min={0}
                              step={1}
                              value={tool.processWaitSec}
                              onChange={(event) =>
                                updateTool(tool.toolId, {
                                  processWaitSec: Math.max(0, Number(event.target.value)),
                                })
                              }
                              aria-label={`Process time for ${tool.toolId}`}
                              className="h-7 min-w-0 flex-1 rounded border border-slate-300 px-2 text-right text-[11px] font-normal tabular-nums"
                            />
                            <span className="text-slate-400">s</span>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          <input
                            type="number"
                            min={1}
                            step={1}
                            value={tool.foupCapacity ?? DEFAULT_TOOL_FOUP_CAPACITY}
                            onChange={(event) =>
                              updateTool(tool.toolId, {
                                foupCapacity: Math.max(1, Math.floor(Number(event.target.value))),
                              })
                            }
                            aria-label={`FOUP capacity for ${tool.toolId}`}
                            className="h-7 w-full rounded border border-slate-300 px-2 text-right text-[11px] font-normal tabular-nums"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!showJson && step === 2 && (
            <div className="space-y-3">
              <ReviewHeading
                title={`Stockers (${stockers.length})`}
                description="Wait time (wall / real seconds), process, inventory, and material-flow properties. Sent as value × simSpeed."
              />
              <div className="grid gap-2 md:grid-cols-2">
                {stockers.map((stocker) => (
                  <article
                    key={stocker.stockerId}
                    className="rounded border border-slate-200 bg-white p-2.5"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <input
                          value={stocker.name}
                          onChange={(event) =>
                            updateStocker(stocker.stockerId, { name: event.target.value })
                          }
                          aria-label={`Name for ${stocker.stockerId}`}
                          className="h-7 w-full rounded border border-slate-300 px-2 text-[11px] font-normal text-slate-900"
                        />
                        <p className="font-mono text-[10px] text-slate-400">{stocker.stockerId}</p>
                      </div>
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-600">
                        {stocker.materialFlow}
                      </span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <InlineNumberEditor
                        label="Wait time (s)"
                        value={stocker.processWaitSec}
                        min={0}
                        step={1}
                        onChange={(processWaitSec) =>
                          updateStocker(stocker.stockerId, {
                            processWaitSec: Math.max(0, processWaitSec),
                          })
                        }
                      />
                      <InlineProcessTypeSelect
                        label="Process type"
                        value={stocker.processType ?? ''}
                        options={processTypes}
                        noneLabel="Pass-through"
                        onChange={(processType) =>
                          updateStocker(stocker.stockerId, {
                            processType: processType || undefined,
                          })
                        }
                      />
                      <InlineCheckboxEditor
                        label="Outside stocker"
                        checked={stocker.outsideStocker}
                        onChange={(outsideStocker) =>
                          updateStocker(stocker.stockerId, { outsideStocker })
                        }
                      />
                      <InlineCheckboxEditor
                        label="Inventory required"
                        checked={stocker.requireInventoryBeforePick !== false}
                        onChange={(requireInventoryBeforePick) =>
                          updateStocker(stocker.stockerId, { requireInventoryBeforePick })
                        }
                      />
                    </div>
                    <div className="mt-2 rounded bg-slate-50 p-1.5 text-[9px] text-slate-500">
                      <span>
                        Raw / processed: {stocker.rawFoupCapacity} / {stocker.processedFoupCapacity}{' '}
                        <DerivedBadge />
                      </span>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}

          {!showJson && step === 3 && (
            <div className="space-y-3">
              <ReviewHeading
                title={`Dedicated OHTs (${dedicatedOhts.length})`}
                description={`Assignments and vehicle properties from ${ohts.length} total OHTs.`}
              />
              {dedicatedOhts.length === 0 ? (
                <p className="rounded border border-slate-200 bg-slate-50 px-3 py-6 text-center text-xs text-slate-500">
                  No dedicated OHTs are included in this request.
                </p>
              ) : (
                <div className="grid gap-2 md:grid-cols-2">
                  {dedicatedOhts.map((oht) => (
                    <article
                      key={oht.ohtId}
                      className="rounded border border-slate-200 bg-white p-2.5"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <input
                            value={oht.name}
                            onChange={(event) => updateOht(oht.ohtId, { name: event.target.value })}
                            aria-label={`Name for ${oht.ohtId}`}
                            className="h-7 w-full rounded border border-slate-300 px-2 text-[11px] font-normal text-slate-900"
                          />
                          <p className="font-mono text-[10px] text-slate-400">{oht.ohtId}</p>
                        </div>
                        <select
                          value={oht.ohtRole ?? 'group'}
                          onChange={(event) =>
                            updateOht(oht.ohtId, {
                              ohtRole: event.target.value as NonNullable<typeof oht.ohtRole>,
                            })
                          }
                          aria-label={`Role for ${oht.ohtId}`}
                          className="h-7 rounded border border-sky-300 bg-sky-50 px-2 text-[9px] font-medium uppercase text-sky-700"
                        >
                          {['entry', 'group', 'interbay', 'outerloop', 'exit'].map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <InlineProcessTypeSelect
                          label="Tool type"
                          value={oht.toolType ?? ''}
                          options={processTypes}
                          noneLabel="Optional"
                          onChange={(toolType) =>
                            updateOht(oht.ohtId, {
                              toolType: toolType || undefined,
                            })
                          }
                        />
                        <InlineNumberEditor
                          label="Cruise speed (m/s)"
                          value={oht.cruiseSpeed}
                          min={0.1}
                          step={0.1}
                          onChange={(cruiseSpeed) =>
                            updateOht(oht.ohtId, { cruiseSpeed: Math.max(0.1, cruiseSpeed) })
                          }
                        />
                      </div>
                      <KeyValueGrid
                        compact
                        values={[
                          ['Fleet (derived)', oht.fleet ?? '—'],
                          ['Separation (derived)', `${oht.minSeparationM} m`],
                          ['Home node (derived)', oht.homeNodeId],
                          ['Path steps (derived)', String(oht.dedicatedPath?.length ?? 0)],
                        ]}
                      />
                      {oht.dedicatedPath && oht.dedicatedPath.length > 0 && (
                        <pre className="mt-2 max-h-24 overflow-auto rounded border border-slate-200 bg-slate-50 p-2 text-[9px] text-slate-600 scrollbar-thin">
                          {JSON.stringify(oht.dedicatedPath, null, 2)}
                        </pre>
                      )}
                    </article>
                  ))}
                </div>
              )}
            </div>
          )}
        </main>

        <footer className="flex items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-4 py-2">
          <div>
            {SHOW_RESET_TO_GENERATED && (
              <button
                type="button"
                onClick={resetToGenerated}
                className="rounded px-2.5 py-1.5 text-[10px] font-normal text-slate-600 hover:bg-slate-200"
              >
                Reset to generated config
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={showJson ? hideJsonView : openJsonView}
              className="rounded px-1 py-1.5 text-[10px] font-normal text-sky-700 underline decoration-sky-400 underline-offset-2 hover:text-sky-900"
            >
              {showJson ? 'Hide JSON' : 'View JSON'}
            </button>
            <button
              type="button"
              onClick={() => (showJson ? hideJsonView() : goToStep(Math.max(0, step - 1)))}
              disabled={!showJson && step === 0}
              className="rounded border border-slate-300 bg-white px-2.5 py-1.5 text-[10px] font-normal text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Back
            </button>
            {showJson ? (
              <button
                type="button"
                onClick={applyJsonOverride}
                disabled={!parsed.request || !jsonEdited}
                className="rounded bg-amber-600 px-3 py-1.5 text-[10px] font-medium text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Apply testing override
              </button>
            ) : (
              <button
                type="button"
                onClick={saveRequest}
                className="rounded bg-emerald-600 px-3 py-1.5 text-[10px] font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Save properties
              </button>
            )}
            {!showJson && step < STEPS.length - 1 && (
              <button
                type="button"
                onClick={() => goToStep(Math.min(STEPS.length - 1, step + 1))}
                className="rounded bg-sky-600 px-3 py-1.5 text-[10px] font-medium text-white hover:bg-sky-700"
              >
                Next
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>,
    document.body
  );
};

const ReviewHeading: React.FC<{ title: string; description: string }> = ({
  title,
  description,
}) => (
  <div>
    <h3 className="text-xs font-medium text-slate-900">{title}</h3>
    <p className="mt-0.5 text-[10px] text-slate-500">{description}</p>
  </div>
);

const NumberEditorCard: React.FC<{
  label: string;
  value: number;
  detail?: string;
  min: number;
  step: number;
  suffix?: string;
  onChange: (value: number) => void;
}> = ({ label, value, detail, min, step, suffix = '', onChange }) => (
  <div className="rounded border border-slate-200 bg-slate-50 p-2">
    <p className="text-[9px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
    <div className="mt-0.5 flex items-center gap-1">
      <input
        type="number"
        value={value}
        min={min}
        step={step}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (Number.isFinite(next) && next >= min) onChange(next);
        }}
        className="h-7 min-w-0 flex-1 rounded border border-slate-300 bg-white px-2 text-right text-xs font-normal tabular-nums text-slate-800"
      />
      {suffix && <span className="text-[10px] text-slate-500">{suffix}</span>}
    </div>
    {detail && <p className="mt-0.5 text-[9px] leading-tight text-slate-500">{detail}</p>}
  </div>
);

const BooleanEditorCard: React.FC<{
  label: string;
  value: boolean;
  detail?: string;
  onChange: (value: boolean) => void;
}> = ({ label, value, detail, onChange }) => (
  <label className="flex min-h-14 cursor-pointer items-center justify-between rounded border border-slate-200 bg-slate-50 p-2">
    <span className="flex flex-col">
      <span className="text-[10px] font-normal text-slate-700">{label}</span>
      {detail && <span className="text-[9px] text-slate-500">{detail}</span>}
    </span>
    <input
      type="checkbox"
      checked={value}
      onChange={(event) => onChange(event.target.checked)}
      className="h-3.5 w-3.5 accent-sky-600"
    />
  </label>
);

const InlineProcessTypeSelect: React.FC<{
  label: string;
  value: string;
  options: readonly string[];
  noneLabel: string;
  onChange: (value: string) => void;
}> = ({ label, value, options, noneLabel, onChange }) => (
  <label className="min-w-0">
    <span className="block text-[9px] font-normal text-slate-500">{label}</span>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="mt-0.5 h-7 w-full rounded border border-slate-300 px-2 text-[11px] font-normal text-slate-700"
    >
      <option value="">{noneLabel}</option>
      {options.map((processType) => (
        <option key={processType} value={processType}>
          {processType}
        </option>
      ))}
    </select>
  </label>
);

const InlineNumberEditor: React.FC<{
  label: string;
  value: number;
  min: number;
  step: number;
  onChange: (value: number) => void;
}> = ({ label, value, min, step, onChange }) => (
  <label>
    <span className="block text-[9px] font-normal text-slate-500">{label}</span>
    <input
      type="number"
      value={value}
      min={min}
      step={step}
      onChange={(event) => {
        const next = Number(event.target.value);
        if (Number.isFinite(next) && next >= min) onChange(next);
      }}
      className="mt-0.5 h-7 w-full rounded border border-slate-300 px-2 text-right text-[11px] font-normal tabular-nums text-slate-700"
    />
  </label>
);

const InlineCheckboxEditor: React.FC<{
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ label, checked, onChange }) => (
  <label className="flex h-7 cursor-pointer items-center justify-between gap-2 rounded border border-slate-200 px-2 text-[9px] font-normal text-slate-600">
    {label}
    <input
      type="checkbox"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      className="h-3 w-3 accent-sky-600"
    />
  </label>
);

const DerivedBadge: React.FC = () => (
  <span className="ml-1 rounded bg-slate-100 px-1 py-0.5 text-[7px] font-medium uppercase text-slate-400">
    Derived
  </span>
);

const KeyValueGrid: React.FC<{
  values: Array<[string, string]>;
  compact?: boolean;
}> = ({ values, compact = false }) => (
  <dl className={`grid gap-x-3 gap-y-1.5 ${compact ? 'mt-2 grid-cols-2' : 'sm:grid-cols-3'}`}>
    {values.map(([label, value]) => (
      <div key={label} className="min-w-0">
        <dt className="text-[9px] font-normal text-slate-400">{label}</dt>
        <dd className="truncate text-[10px] font-normal text-slate-700" title={value}>
          {value}
        </dd>
      </div>
    ))}
  </dl>
);

export default ReviewSimulationConfigModal;
